import { EntityStatus, getStatusForId } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import {
  AllCampaign,
  Campaign,
  CampaignReference,
  CampaignScope,
  CampaignScopeType,
  CampaignWithReference,
  RequestStatusType,
  YammerState,
} from '../../state/types';
import { createFindById } from '../findById';
import { getStatus as getRequestStatus } from '../requestStatuses/selectors';

import { campaignsListRequestStatusKey } from './loadMoreAllCampaignsActions.types';
import { AllCampaignsLoadOptions } from './shared.types';

export const findById = createFindById<AllCampaign>('allCampaigns');

const getCampaignScopeKey = (scope?: CampaignScope) => {
  switch (scope?.type) {
    case CampaignScopeType.GROUP:
      return `-${CampaignScopeType.GROUP}_${scope.groupId}`;
    case CampaignScopeType.NETWORK:
      return `-${CampaignScopeType.NETWORK}`;
    default:
      return '';
  }
};

type GetAllCampaignsKey = (options: AllCampaignsLoadOptions) => string;
export const getAllCampaignsKey: GetAllCampaignsKey = ({ sortBy, filterByState, scope }) =>
  `${sortBy}-${filterByState}${getCampaignScopeKey(scope)}`;

type GetAllCampaignsStatus = (state: YammerState, options: AllCampaignsLoadOptions) => EntityStatus;
export const getAllCampaignsStatus: GetAllCampaignsStatus = (state, options) =>
  getStatusForId(state.allCampaigns, getAllCampaignsKey(options));

const emptyCampaignIds: Campaign['id'][] = [];
export const getAllCampaignsIdsFromState = (state: YammerState, options: AllCampaignsLoadOptions) =>
  findById(state, getAllCampaignsKey(options))?.campaignIds ?? emptyCampaignIds;

type GetFulfilledCampaigns = (state: YammerState) => Record<string, Campaign>;
export const getFulfilledCampaigns: GetFulfilledCampaigns = (state) => state.campaigns.fulfilled.byId;

type GetFulfilledCampaignReferences = (state: YammerState) => Record<string, CampaignReference>;
export const getFulfilledCampaignReferences: GetFulfilledCampaignReferences = (state) =>
  state.campaignReferences.fulfilled.byId;

type GetCampaigns = (state: YammerState, options: AllCampaignsLoadOptions) => CampaignWithReference[];
export const getCampaigns: GetCampaigns = createCachedSelector(
  getAllCampaignsIdsFromState,
  getFulfilledCampaigns,
  getFulfilledCampaignReferences,
  (allCampaignIds, fulfilledCampaigns, fulfilledCampaignReferences) =>
    allCampaignIds.map((id) => {
      const campaign = fulfilledCampaigns[id];
      if (!campaign) {
        throw new Error('Missing campaign data in state for all campaign edge.');
      }

      return {
        ...fulfilledCampaigns[id],
        ...fulfilledCampaignReferences[id],
      };
    })
)((_state, options) => getAllCampaignsKey(options));

type GetLoadMoreCampaignsListStatus = (state: YammerState) => EntityStatus;
export const getLoadMoreCampaignsListStatus: GetLoadMoreCampaignsListStatus = (state) =>
  getRequestStatus(state, RequestStatusType.CampaignsListMoreRequest, campaignsListRequestStatusKey);

type HasNextPageOfCampaignsListAvailable = (state: YammerState, options: AllCampaignsLoadOptions) => boolean;
export const hasNextPageOfCampaignsListAvailable: HasNextPageOfCampaignsListAvailable = (state, options) =>
  findById(state, getAllCampaignsKey(options))?.hasNextPage ?? false;
